/**
 * ErrorDetailsDrawer (PHASE 7)
 * ============================
 * Reusable drawer for showing structured universal error schema details.
 */

import React, { useMemo } from 'react';

import { X, Copy, AlertTriangle } from 'lucide-react';

import { summarizeUniversalError } from '@/pages/intelligence-studio/utils/universalError';

export default function ErrorDetailsDrawer({ open, onClose, error, title }) {
  const summary = useMemo(() => {
    try {
      if (!error) return { title: title || 'Error details', summary: '' };
      const s = summarizeUniversalError(error);
      return { title: title || s.title, summary: s.summary };
    } catch {
      return { title: title || 'Error details', summary: '' };
    }
  }, [error, title]);

  const detailsJson = useMemo(() => {
    try {
      if (!error) return '';
      const obj = {
        code: error.code,
        message: error.message,
        source: error.source,
        httpStatus: error.httpStatus,
        timestamp: error.timestamp,
        details: error.details,
      };
      return JSON.stringify(obj, null, 2);
    } catch {
      return '';
    }
  }, [error]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close error details"
      />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-neutral-900 border-l border-slate-200 dark:border-neutral-700 shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-neutral-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-semibold text-slate-800 dark:text-neutral-100 truncate">
                {summary.title}
              </h3>
            </div>
            {summary.summary ? (
              <p className="mt-1 text-xs text-slate-500 dark:text-neutral-400 whitespace-pre-wrap">
                {summary.summary}
              </p>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800"
              onClick={() => {
                try {
                  if (!detailsJson) return;
                  navigator.clipboard.writeText(detailsJson);
                } catch {}
              }}
              title="Copy details"
              aria-label="Copy error details"
            >
              <Copy className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
            </button>
            <button
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-800"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <X className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <div className="text-[11px] font-mono whitespace-pre-wrap break-words text-slate-700 dark:text-neutral-200">
            {detailsJson || 'No details available.'}
            {error?.stack ? `\n\n---\nstack:\n${String(error.stack)}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

